import { useState } from "react";
import { ExternalLink, Plus, Star, Trash2 } from "lucide-react";
import { PageHeader } from "@/components/AppShell";
import { Sheet } from "@/components/Sheet";
import { Badge, Button, Card, cx, EmptyState, Field, NumberStepper, PageLoader, Segmented, TextField } from "@/components/ui";
import { Confetti, IconPicker, Points, ProgressBar } from "@/components/bits";
import { api } from "@/lib/api";
import { useAction, useGoals, useKidSummary, useRewardTypes } from "@/lib/queries";
import { useSession } from "@/lib/session";
import type { GoalDTO } from "@shared/types";

const GOAL_ICONS = ["⭐", "🚲", "🎮", "🧱", "📱", "🎧", "⚽", "🛹", "🎨", "📚", "🧸", "👟", "🎸", "🏕️", "🎢", "🐶"];

interface Draft {
  id?: string;
  name: string;
  icon: string;
  url: string;
  priceMode: "points" | "cash";
  price: number;
  priceCash: number;
}

function GoalSheet({ draft, setDraft }: { draft: Draft; setDraft: (d: Draft | null) => void }) {
  const { family } = useSession();
  const rewards = useRewardTypes();
  const cash = rewards.data?.rewardTypes.find((r) => r.kind === "cash" && r.active && r.pointsPerUnit);
  const points = draft.priceMode === "cash" && cash ? Math.ceil(draft.priceCash * cash.pointsPerUnit!) : draft.price;
  const save = useAction(
    () => {
      const body = {
        name: draft.name,
        icon: draft.icon,
        url: draft.url.trim() || null,
        price: points,
        priceCash: draft.priceMode === "cash" ? draft.priceCash : null,
      };
      return draft.id ? api.patch(`/goals/${draft.id}`, body) : api.post("/goals", body);
    },
    { success: draft.id ? "Goal updated" : "Goal added! Start saving 💪", onSuccess: () => setDraft(null) },
  );
  const remove = useAction(() => api.del(`/goals/${draft.id}`), { success: "Goal removed", onSuccess: () => setDraft(null) });
  const validUrl = !draft.url.trim() || /^https?:\/\//i.test(draft.url.trim());
  return (
    <Sheet
      open
      onClose={() => setDraft(null)}
      title={draft.id ? "Edit goal" : "New goal"}
      footer={
        <div className="flex gap-2">
          {draft.id && (
            <Button variant="secondary" aria-label="Remove goal" onClick={() => remove.mutate()} loading={remove.isPending}>
              <Trash2 className="size-4 text-danger" />
            </Button>
          )}
          <Button block onClick={() => save.mutate()} loading={save.isPending} disabled={!draft.name.trim() || points < 1 || !validUrl}>
            Save goal
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        <TextField label="What do you want?" placeholder="New bike" value={draft.name} maxLength={80} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
        <Field label="Icon">
          <IconPicker value={draft.icon} onChange={(icon) => setDraft({ ...draft, icon })} choices={GOAL_ICONS} />
        </Field>
        {cash && (
          <Segmented
            value={draft.priceMode}
            onChange={(priceMode) => setDraft({ ...draft, priceMode })}
            options={[
              { value: "cash", label: "Price in $" },
              { value: "points", label: `Price in ${family.currencyName}` },
            ]}
          />
        )}
        {draft.priceMode === "cash" && cash ? (
          <Field label="How much does it cost?" hint={`= ${points.toLocaleString()} ${family.currencyName}`}>
            <NumberStepper value={draft.priceCash} onChange={(priceCash) => setDraft({ ...draft, priceCash })} min={1} max={100000} step={5} suffix="$" />
          </Field>
        ) : (
          <Field label={`Price in ${family.currencyName}`}>
            <NumberStepper value={draft.price} onChange={(price) => setDraft({ ...draft, price: Math.round(price) })} min={1} max={10000000} step={10} />
          </Field>
        )}
        <TextField
          label="Link (optional)"
          placeholder="https://www.amazon.com/…"
          inputMode="url"
          value={draft.url}
          onChange={(e) => setDraft({ ...draft, url: e.target.value })}
          error={validUrl ? null : "Links start with https://"}
          hint="Paste a link so your parent can find it. Parents always approve before anything is bought."
        />
      </div>
    </Sheet>
  );
}

function GoalCard({ goal, onEdit, weekly }: { goal: GoalDTO; onEdit: () => void; weekly: number }) {
  const { family } = useSession();
  const [cheer, setCheer] = useState(0);
  const request = useAction(() => api.post(`/goals/${goal.id}/request`), {
    success: "Asked a parent to buy it! 🎉",
    onSuccess: () => setCheer((n) => n + 1),
  });
  const primary = useAction(() => api.patch(`/goals/${goal.id}`, { isPrimary: true }));
  const remaining = Math.max(0, Math.round(goal.price * (1 - goal.progress)));
  const weeks = weekly > 0 && remaining > 0 ? Math.ceil(remaining / weekly) : null;
  const ready = goal.progress >= 1 && goal.status === "active";
  return (
    <Card className={cx("p-4", goal.isPrimary && "ring-2 ring-coin/60")}>
      {cheer > 0 && <Confetti key={cheer} />}
      <div className="flex items-start gap-3">
        <button type="button" onClick={onEdit} className="flex size-14 shrink-0 items-center justify-center rounded-2xl bg-coin-soft text-3xl" aria-label={`Edit ${goal.name}`}>
          {goal.icon}
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <button type="button" onClick={onEdit} className="truncate text-left text-lg font-extrabold">
              {goal.name}
            </button>
            {goal.url && (
              <a href={goal.url} target="_blank" rel="noreferrer noopener" className="text-faint hover:text-brand" aria-label="Open link">
                <ExternalLink className="size-4" />
              </a>
            )}
          </div>
          <div className="text-sm text-muted">
            <Points value={goal.price} family={family} />
            {goal.priceCash ? ` · $${goal.priceCash.toFixed(2)}` : ""}
          </div>
        </div>
        <button
          type="button"
          aria-label={goal.isPrimary ? "Main goal" : "Make this my main goal"}
          onClick={() => !goal.isPrimary && primary.mutate()}
          className={cx("rounded-full p-2", goal.isPrimary ? "text-coin" : "text-faint hover:text-coin")}
        >
          <Star className="size-5" fill={goal.isPrimary ? "currentColor" : "none"} />
        </button>
      </div>
      <div className="mt-3">
        <ProgressBar value={goal.progress} color={ready ? "var(--brand)" : "var(--coin)"} />
        <div className="mt-1.5 flex justify-between text-xs font-bold text-muted">
          <span>{Math.round(goal.progress * 100)}% saved</span>
          <span>{remaining > 0 ? `${remaining.toLocaleString()} to go${weeks ? ` · ~${weeks} wk${weeks === 1 ? "" : "s"} at this pace` : ""}` : "You can afford it!"}</span>
        </div>
      </div>
      {goal.status === "requested" ? (
        <Badge tone="warn" className="mt-3 px-3 py-1.5 text-sm">
          Waiting for a parent to approve
        </Badge>
      ) : goal.status === "approved" ? (
        <Badge tone="coin" className="mt-3 px-3 py-1.5 text-sm">
          Approved — it's on the way!
        </Badge>
      ) : goal.status === "purchased" ? (
        <Badge tone="brand" className="mt-3 px-3 py-1.5 text-sm">
          Got it! 🎉
        </Badge>
      ) : (
        ready && (
          <Button className="mt-3" block variant="coin" onClick={() => request.mutate()} loading={request.isPending}>
            Ask a parent to buy it
          </Button>
        )
      )}
    </Card>
  );
}

export function KidGoals() {
  const { user } = useSession();
  const goals = useGoals();
  const summary = useKidSummary(user.id);
  const [draft, setDraft] = useState<Draft | null>(null);

  if (!goals.data || !summary.data) return <PageLoader />;
  const list = goals.data.goals;
  const active = list.filter((g) => g.status !== "purchased");
  const done = list.filter((g) => g.status === "purchased");
  const newDraft = (): Draft => ({ name: "", icon: "⭐", url: "", priceMode: "cash", price: 100, priceCash: 20 });

  return (
    <div>
      <PageHeader
        title="Goals"
        subtitle="Save up for what matters to you"
        bell="/kid/notifications"
        actions={
          <Button size="sm" onClick={() => setDraft(newDraft())}>
            <Plus className="size-4" /> Add
          </Button>
        }
      />
      <div className="mt-4 space-y-3">
        {active.length === 0 ? (
          <EmptyState icon="🎯" title="What are you saving for?" action={<Button onClick={() => setDraft(newDraft())}>Add a goal</Button>}>
            Add something you really want and watch your savings climb toward it.
          </EmptyState>
        ) : (
          active.map((g) => (
            <GoalCard
              key={g.id}
              goal={g}
              weekly={summary.data.earnedThisWeek}
              onEdit={() =>
                g.status === "active" &&
                setDraft({
                  id: g.id,
                  name: g.name,
                  icon: g.icon,
                  url: g.url ?? "",
                  priceMode: g.priceCash ? "cash" : "points",
                  price: g.price,
                  priceCash: g.priceCash ?? 0,
                })
              }
            />
          ))
        )}
      </div>
      {done.length > 0 && (
        <div className="mt-8">
          <h2 className="mb-2 px-1 text-lg font-extrabold">Achieved</h2>
          <div className="flex flex-wrap gap-2">
            {done.map((g) => (
              <span key={g.id} className="rounded-full bg-brand-soft px-3 py-1.5 text-sm font-bold text-brand-strong">
                {g.icon} {g.name}
              </span>
            ))}
          </div>
        </div>
      )}
      {draft && <GoalSheet draft={draft} setDraft={setDraft} />}
    </div>
  );
}
